import React, { useCallback, useMemo, useState } from 'react';
import { FeedbackCommentProps, RenderQuestionProps } from '../utils';
import {
  IndicatorByService,
  IndicatorWithUpdate,
  indicatorSectionsInOrder,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import ImpactDetailStageIcons, {
  CommentDetails,
  CommentsByStage,
  ImpactDetailStageIconsProps,
  parseFeedbackCommentsWithDetails,
} from '../FeedbackImpactDetails/ImpactDetailStageIcons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

export type CommentsByStageIndicatorWithUpdate = CommentsByStage<
  IndicatorWithUpdate,
  IndicatorWithUpdate['id']
>;

export const indicatorWithUpdateKeys = (): (keyof IndicatorWithUpdate)[] => [
  'name',
];
export const getIndicatorWithUpdateId = (
  indicator: IndicatorWithUpdate,
): number => indicator.id;
export const getIndicatorWithUpdateTitle = (
  key: keyof IndicatorWithUpdate,
): string => {
  switch (key) {
    case 'name':
      return 'Indicator';
    default:
      return 'Others';
  }
};

const indicatorNameStyle: React.CSSProperties = { marginBottom: SPACERS.tiny };
export const getIndicatorItemTitle = (
  indicator: IndicatorWithUpdate,
): React.ReactNode => (
  <div style={indicatorNameStyle}>
    <strong>Indicator Name: </strong>
    {indicator.name}
  </div>
);

export const getIndicatorWithUpdateDescription = (
  key: keyof IndicatorWithUpdate,
  indicator: IndicatorWithUpdate,
): React.ReactNode => {
  switch (key) {
    case 'name':
      return indicator.name;
    default:
      return indicator[key]?.toString() || 'Unknown';
  }
};

const style = { marginTop: SPACERS.tiny };
export const getIndicatorWithUpdateExtraDetails = (
  key: keyof IndicatorWithUpdate,
  indicator: IndicatorWithUpdate,
): React.ReactNode => {
  switch (key) {
    case 'name':
      return (
        <>
          <TrueImpactText>
            <div style={style}>
              <strong>Given Definition:</strong> {indicator.updatedText}
            </div>
            <div style={style}>
              <strong>Default Definition:</strong> {indicator.definition}
            </div>
          </TrueImpactText>
        </>
      );
    default:
      return null;
  }
};

export const parseCommentDetailsWithIndicatorWithUpdate = (
  details?: string,
): CommentDetails<IndicatorWithUpdate> | undefined => {
  if (!details) return undefined;
  try {
    const d: CommentDetails<IndicatorWithUpdate> = JSON.parse(details);
    if (d.indicatorId && d.sectionKey && d.subQuestion) return d;
    return undefined;
  } catch (e) {
    return undefined;
  }
};

const parseAnswer = (text: string): IndicatorByService | undefined => {
  try {
    const result = JSON.parse(text);
    return Array.isArray(result) ? undefined : result;
  } catch (e) {
    return undefined;
  }
};

const FeedbackImpactSummary: React.FC<RenderQuestionProps> = ({
  answer,
  comments,
  addOrUpdateComment,
  commentsLength,
  currentUserEmail,
  currentUserId,
  currentUserName,
  currentUserType,
  handleOpen,
  title,
  questionId,
  reportId,
  className,
}) => {
  const [commentsByStage, setCommentsByStage] = useState<
    CommentsByStageIndicatorWithUpdate
  >(
    parseFeedbackCommentsWithDetails(
      comments,
      parseCommentDetailsWithIndicatorWithUpdate,
    ),
  );
  const indicators = useMemo(() => parseAnswer(answer.text), [answer.text]);

  const getComments: ImpactDetailStageIconsProps<
    IndicatorWithUpdate
  >['getComments'] = useCallback(
    ({ sectionKey, indicatorId, subQuestion }): FeedbackCommentProps[] =>
      commentsByStage[sectionKey]?.[indicatorId]?.[subQuestion] || [],
    [commentsByStage],
  );

  if (!indicators) {
    return (
      <TrueImpactText
        variant="body-bold"
        color="critical"
        className={className}
      >
        Unable to locate Impact Model Builder. Please contact support about this
        report.
      </TrueImpactText>
    );
  }
  return (
    <div className={className}>
      {indicatorSectionsInOrder.map(key => (
        <ImpactDetailStageIcons<IndicatorWithUpdate>
          reportId={reportId}
          questionId={questionId}
          currentUserName={currentUserName}
          currentUserId={currentUserId}
          currentUserType={currentUserType}
          currentUserEmail={currentUserEmail}
          sectionId="impact_summary"
          sectionKey={key}
          options={indicators[key]}
          itemTitle={getIndicatorItemTitle}
          getIndicatorId={getIndicatorWithUpdateId}
          getTitle={getIndicatorWithUpdateTitle}
          getComments={getComments}
          getDescription={getIndicatorWithUpdateDescription}
          getExtraDetails={getIndicatorWithUpdateExtraDetails}
          getKeys={indicatorWithUpdateKeys}
          setCommentsByStage={setCommentsByStage}
        />
      ))}
    </div>
  );
};

export default FeedbackImpactSummary;
